// A recording as a waveform you can play: the played part in ink, the rest gray, an amber playhead while it plays,
// and numbered marks (mistakes) you can tap to jump to. Plays `src` when given. Without it (sample data) the clock
// runs on its own, so the screen can be tried before recordings exist.
import { useEffect, useRef, useState } from 'react';
import { cx } from '@/lib/cx';
import { formatClock } from '@/lib/format';
import { Button } from './Button';
import { Icon } from './Icon';

export type WaveMark = { at: number; label: string };

/** Drawn until real peaks are passed: an even, speech-like shape. */
const SHAPE = Array.from({ length: 48 }, (_, i) => 0.3 + 0.7 * Math.abs(Math.sin(i * 1.3) * Math.cos(i * 0.37)));

type WaveformPlayerProps = {
  src?: string;
  /** Length in seconds, shown before the audio loads. */
  duration: number;
  /** Bar heights from 0 to 1, from start to end. */
  peaks?: readonly number[];
  /** Numbered in order: 1, 2, 3… */
  marks?: readonly WaveMark[];
  /** Called with the mark's index after jumping to it. */
  onMark?: (index: number) => void;
  /** What is playing, for screen readers: "your answer". */
  label: string;
  /** A bigger play button, when playing is the task (Listen). */
  large?: boolean;
  /** Playback speed, e.g. 1 or 1.25. */
  rate?: number;
  /** Starting position in seconds. */
  position?: number;
  /** Shows a button that goes back this many seconds (Listen). */
  back?: number;
};

export function WaveformPlayer({
  src,
  duration,
  peaks = SHAPE,
  marks = [],
  onMark,
  label,
  large,
  rate = 1,
  position = 0,
  back,
}: WaveformPlayerProps) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(position);
  // The real length once the audio has loaded; `duration` until then.
  const [loaded, setLoaded] = useState<number | null>(null);
  const length = loaded ?? duration;

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = rate;
  }, [rate]);

  // No audio yet: move the clock while "playing", and stop at the end.
  useEffect(() => {
    if (src || !playing) return;
    const id = setInterval(() => setTime((t) => Math.min(duration, t + 0.25 * rate)), 250);
    return () => clearInterval(id);
  }, [src, playing, duration, rate]);
  useEffect(() => {
    if (!src && time >= duration) setPlaying(false);
  }, [src, time, duration]);

  const toggle = () => {
    const el = audio.current;
    if (el) {
      if (el.paused) void el.play();
      else el.pause();
      return;
    }
    if (!playing && time >= duration) setTime(0);
    setPlaying(!playing);
  };

  const jump = (seconds: number) => {
    setTime(seconds);
    if (audio.current) audio.current.currentTime = seconds;
  };

  const progress = length ? Math.min(1, time / length) : 0;
  const played = Math.round(progress * peaks.length);

  return (
    <div className={cx('wave-player', large && 'lg', marks.length > 0 && 'with-marks')}>
      <div className="wave-row">
        <button
          className={cx('play', playing && 'on')}
          type="button"
          aria-label={`${playing ? 'Pause' : 'Play'} ${label}`}
          onClick={toggle}
        >
          <Icon name={playing ? 'pause' : 'play'} />
        </button>
        <div className="wave">
          <div className="wave-bars" aria-hidden="true">
            {peaks.map((p, i) => (
              <i
                // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional in time.
                key={i}
                className={i < played ? 'played' : undefined}
                style={{ height: `${Math.round(12 + Math.max(0, Math.min(1, p)) * 88)}%` }}
              />
            ))}
          </div>
          {(playing || time > 0) && (
            <span className="playhead" style={{ left: `${progress * 100}%` }} aria-hidden="true" />
          )}
          {marks.map((m, i) => (
            <button
              key={`${m.at}-${m.label}`}
              className="wave-mark"
              type="button"
              style={{ left: `${length ? (m.at / length) * 100 : 0}%` }}
              aria-label={`${m.label} ${i + 1} at ${formatClock(m.at)}`}
              onClick={() => {
                jump(m.at);
                onMark?.(i);
              }}
            >
              <span>{i + 1}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="wave-foot">
        <span className="num">
          {formatClock(time)} / {formatClock(length)}
        </span>
        {marks.length > 0 && <span>Tap a number to jump there</span>}
        {back !== undefined && (
          <Button variant="ghost" size="sm" icon="rotate-ccw" onClick={() => jump(Math.max(0, time - back))}>
            Back {back} s
          </Button>
        )}
      </div>
      {src && (
        // biome-ignore lint/a11y/useMediaCaption: speech practice audio; the transcript is shown on the page.
        <audio
          ref={audio}
          src={src}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const seconds = e.currentTarget.duration;
            if (Number.isFinite(seconds) && seconds > 0) setLoaded(seconds);
            e.currentTarget.playbackRate = rate;
          }}
        />
      )}
    </div>
  );
}
